import { BankAccountsContext } from '@app/collections/fortress/BankAccountsContext'
import { Col, Description, DescriptionItem, Divider, Row, Select } from '@app/components'
import { IStore } from '@app/redux'
import { getSingle } from '@app/services'
import React, { useContext, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'

const euCountries = new Set([
  'AT',
  'BE',
  'BG',
  'HR',
  'CY',
  'CZ',
  'DK',
  'EE',
  'FI',
  'FR',
  'DE',
  'GR',
  'HU',
  'IE',
  'IT',
  'LV',
  'LT',
  'LU',
  'MT',
  'NL',
  'PL',
  'PT',
  'RO',
  'SK',
  'SI',
  'ES',
  'SE',
])

export const IntlTransfer: React.FC<any> = ({ userMemo }) => {
  const { fetchAccounts } = useContext(BankAccountsContext)
  const [transactionType, setTransactionType] = useState('EU-instructions')

  useEffect(() => {
    fetchAccounts()
    getSingleCountry()
  }, [])

  const getSingleCountry = async () => {
    const single = await getSingle(user.uid)
    const country = single?.country

    if (country === 'CA') {
      setTransactionType('CA-instructions')
    } else if (country === 'UK') {
      setTransactionType('UK-instructions')
    } else if (country && euCountries.has(country)) {
      setTransactionType('EU-instructions')
    } else {
      setTransactionType('SWIFT-instructions')
    }
  }

  const [bankDetails, setBankDetails] = useState<any>({
    accountNumber: '',
    routingNumber: '',
    recipientName: '',
    recipientAddress: '',
    bankName: '',
    memo: '',
  })
  // Function to get bank details based on the last character of userMemo
  const getBankDetails = (userMemo: string, bankType: string) => {
    if (userMemo) {
      if (bankType == 'EU-instructions') {
        return {
          accountHolder: 'CTS Global LLC',
          iban: 'GB30TCCL00997954760168',
          swift: 'TCCLGB31',
          bank: 'The Currency Cloud Limited',
          bankAddress: '12 Steward Street, The Steward Building, London, E1 6FQ, GB',
          bankCountry: 'United Kingdom (GB)',
          memo: userMemo,
        }
      } else if (bankType == 'UK-instructions') {
        return {
          accountHolder: 'CTS Global LLC',
          sortCode: '041404',
          accountNumber: '68708994',
          bank: 'The Currency Cloud Limited',
          bankAddress: '12 Steward Street, The Steward Building, London, E1 6FQ, GB',
          bankCountry: 'United Kingdom (GB)',
          memo: userMemo,
        }
      } else if (bankType == 'CA-instructions') {
        return {
          accountHolder: 'CTS Global LLC',
          bankCode: '352',
          transitCode: '10009',
          accountNumber: '997391321',
          bank: 'Digital Commerce Bank',
          bankAddress: '736 Meridian Road N.E, Calgary, Alberta, CA',
          bankCountry: 'Canada (CA)',
          memo: userMemo,
        }
      } else if (bankType == 'SWIFT-instructions') {
        return {
          accountHolder: 'CTS Global LLC',
          swift: 'TCCLGB3L',
          iban: 'GB04TCCL04140468708994',
          intermediaryBank: 'BARCLAYS BANK PLC',
          intermediaryBankSwift: 'BARCAEADXXX',
          memo: userMemo,
        }
      } else {
        const lastChar = userMemo.slice(-1) // Get the last character of userMemo
        if (/[A-M]/.test(lastChar)) {
          // Last character is a-z
          return {
            //PNC Bank Details
            accountNumber: '4698968264',
            routingNumber: '043000096',
            //Update Address
            recipientName: 'CTS Global',
            recipientAddress: '1001 S. Main St, Kalispell, Montana, 59901',
            bankName: 'PNC Bank',
            memo: userMemo,
          }
        } else if (/[0-9]/.test(lastChar)) {
          // Last character is 0-9
          return {
            //Chase Bank Details
            accountNumber: '2901988958',
            routingNumber: '111000614',
            //Update Address
            recipientName: 'CTS Global',
            recipientAddress: '1001 S. Main St, Kalispell, Montana, 59901',
            bankName: 'Chase Bank',
            memo: userMemo,
          }
        } else if (/[N-Z]/.test(lastChar)) {
          // Last character is N-Z
          return {
            //Wells Fargo Bank Details
            accountNumber: '7729151519',
            routingNumber: '121000248',
            //Update Address
            recipientName: 'CTS Global',
            recipientAddress: '1001 S. Main St, Kalispell, Montana, 59901',
            bankName: 'Wells Fargo Bank',
            memo: userMemo,
          }
        }
      }
    } else {
      // Default or invalid character handling
      return {
        accountNumber: '',
        routingNumber: '',
        recipientName: '',
        recipientAddress: '',
        bankName: '',
        memo: '',
      }
    }
  }

  // Get the bank details based on userMemo
  useEffect(() => {
    const tmpDetails = getBankDetails(userMemo, transactionType)
    //console.log(bankDetails);
    setBankDetails(tmpDetails)
  }, [userMemo, transactionType])

  const { user } = useSelector((state: IStore) => state)

  return (
    <>
      <Row gutter={[10, 10]}>
        <Col span={24}>
          <Row gutter={[10, 10]}>
            <Col span={24}>
              <Select
                width="100%"
                placeholder="Select transfer type"
                value={transactionType}
                options={[
                  { value: 'EU-instructions', label: 'EU' },
                  { value: 'CA-instructions', label: 'Canada' },
                  { value: 'UK-instructions', label: 'UK' },
                  { value: 'SWIFT-instructions', label: 'Swift' },
                ]}
                onChange={(v) => setTransactionType(v)}
              />
            </Col>
          </Row>

          <Divider />

          <Row gutter={[10, 10]}>
            <Col span={24}>
              <>
                <Description column={1}>
                  <DescriptionItem label="Account Holder">{bankDetails.accountHolder}</DescriptionItem>
                  {transactionType == 'EU-instructions' && (
                    <>
                      <DescriptionItem label="IBAN">{bankDetails.iban}</DescriptionItem>
                      <DescriptionItem label="Swift/BIC">{bankDetails.swift}</DescriptionItem>
                      <DescriptionItem label="Bank">{bankDetails.bank}</DescriptionItem>
                      <DescriptionItem label="Bank Address">{bankDetails.bankAddress}</DescriptionItem>
                    </>
                  )}
                  {transactionType == 'UK-instructions' && (
                    <>
                      <DescriptionItem label="Sort Code">{bankDetails.sortCode}</DescriptionItem>
                      <DescriptionItem label="Account Number">{bankDetails.accountNumber}</DescriptionItem>
                      <DescriptionItem label="Bank">{bankDetails.bank}</DescriptionItem>
                      <DescriptionItem label="Bank Address">{bankDetails.bankAddress}</DescriptionItem>
                    </>
                  )}
                  {transactionType == 'CA-instructions' && (
                    <>
                      <DescriptionItem label="Institution Number">{bankDetails.bankCode}</DescriptionItem>
                      <DescriptionItem label="Transit Number">{bankDetails.transitCode}</DescriptionItem>
                      <DescriptionItem label="Account Number">{bankDetails.accountNumber}</DescriptionItem>
                      <DescriptionItem label="Bank Address">{bankDetails.bankAddress}</DescriptionItem>
                      <DescriptionItem label="Bank Country">{bankDetails.bankCountry}</DescriptionItem>
                    </>
                  )}
                  {transactionType == 'SWIFT-instructions' && (
                    <>
                      <DescriptionItem label="BIC/SWIFT">{bankDetails.swift}</DescriptionItem>
                      <DescriptionItem label="IBAN">{bankDetails.iban}</DescriptionItem>
                      <DescriptionItem label="Account Name">{bankDetails.accountHolder}</DescriptionItem>
                      <DescriptionItem label="Intermediary Bank">{bankDetails.intermediaryBank}</DescriptionItem>
                      <DescriptionItem label="Intermediary Bank SWIFT BIC">
                        {bankDetails.intermediaryBankSwift}
                      </DescriptionItem>
                      <DescriptionItem label="Note">
                        {
                          'In order to avoid inadvertent currency conversions, please indicate the intermediary bank details if possible, and note "DO NOT CONVERT" on the payment reference field.'
                        }
                      </DescriptionItem>
                    </>
                  )}
                  <DescriptionItem label="Memo/Reference (Try to use)">{bankDetails.memo}</DescriptionItem>
                  <DescriptionItem label="Email">{'info@ctsglobal.io'}</DescriptionItem>
                </Description>
              </>
            </Col>
          </Row>
        </Col>
      </Row>
    </>
  )
}
